import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {useEffect, useContext, useCallback, useMemo, useState, memo, Suspense} from 'react'

import {getChangesArg} from '../../../../rest/arguments'
import {restApi} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import type {ChangeId, BuildTypeId} from '../../../../types'
import {emptyArray, noop} from '../../../../utils/empty'
import Grid from '../../../common/Grid/Grid'
import RowGroup from '../../../common/Grid/RowGroup'
import Pager from '../../../common/Pager/Pager.container'
import {PagerGroup} from '../../../common/Pager/Pager.types'
import {useSequenceLoaderReadyHandler} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../../../common/SequenceLoader/SequenceLoader.types'
import Change from '../Change/Change'
import {ChangeContext, ChangeView} from '../Change/Change.context'
import {useChangesFilter} from '../Changes.hooks'
import changesLogStyles from '../ChangesLog/ChangesLog.css'

import ChangeDetailsPopup from './ChangeDetailsPopup/ChangeDetailsPopup'

type InnerProps = {
  buildTypeId?: BuildTypeId | null | undefined
  changes: Array<ChangeId>
  expandedChange: ChangeId | null
  toggleExpandedChange: (id: ChangeId) => void
}
function ChangesListInner({
  buildTypeId,
  changes,
  expandedChange,
  toggleExpandedChange,
}: InnerProps) {
  const {filterOptions} = useChangesFilter(buildTypeId)
  useSequenceLoaderReadyHandler(true)
  const {showFiles, withExpandableLines, withRevision, withChangesActions} =
    useContext(ChangeContext)

  return (
    <Grid
      className={classNames(changesLogStyles.grid, {
        [changesLogStyles.showRevision]: withRevision,
        [changesLogStyles.showActions]: withChangesActions,
      })}
      gridId="changesList"
    >
      {changes.map(changeId => (
        <RowGroup key={stringifyId(changeId)} span={2} gridId="changesList">
          <Change
            expanded={showFiles || (withExpandableLines && expandedChange === changeId)}
            changeId={changeId}
            buildTypeId={buildTypeId}
            toggleExpandedChange={toggleExpandedChange}
            withAuthor={
              (filterOptions.username == null || filterOptions.username === '') &&
              (filterOptions.userId == null || filterOptions.userId === '')
            }
          />
        </RowGroup>
      ))}
    </Grid>
  )
}

type Props = SeqProps & {
  className?: string
  locator: string | null
  buildTypeId?: BuildTypeId | null | undefined
  visibleCount?: number
  onShowChangeDetails?: () => unknown
  onHideChangeDetails?: () => unknown
  withPager?: boolean
}

function ChangesList({
  className,
  buildTypeId,
  locator,
  visibleCount,
  onShowChangeDetails = noop,
  onHideChangeDetails = noop,
  withPager,
}: Props) {
  const {showFiles, withExpandableLines, changeView} = useContext(ChangeContext)

  const changesArg = useMemo(
    () =>
      locator != null
        ? getChangesArg(locator, {withFiles: showFiles, withUpdatePager: withPager})
        : null,
    [locator, showFiles, withPager],
  )

  const [fetchChanges, {changes, changesLoading, changesReloading}] =
    restApi.endpoints.getAllChanges.useLazyQuery({
      selectFromResult: ({isLoading, data, currentData}) => ({
        changes: data?.changes?.result ?? emptyArray,
        changesLoading: isLoading,
        changesReloading: data !== currentData,
      }),
    })

  useEffect(() => {
    if (changesArg != null) {
      fetchChanges(changesArg, true)
    }
  }, [changesArg, fetchChanges])

  const [expandedChange, setExpandedChange] = useState<ChangeId | null>(null)
  const toggleExpandedChange = useCallback(
    (id: ChangeId) => {
      if (expandedChange === id) {
        setExpandedChange(null)
        onHideChangeDetails()
      } else {
        setExpandedChange(id)
        onShowChangeDetails()
      }
    },
    [expandedChange, setExpandedChange, onShowChangeDetails, onHideChangeDetails],
  )

  const isNoChanges = changes.length === 0 && !changesLoading
  useSequenceLoaderReadyHandler(isNoChanges)

  const classes = classNames(className, {
    [changesLogStyles.overlay]: changesReloading,
    [changesLogStyles.preview]: changeView === ChangeView.PREVIEW,
    [changesLogStyles.overall]: changeView === ChangeView.OVERALL,
  })

  const renderLoader = () => (
    <div className={changesLogStyles.loading}>
      <LoaderInline>{'Loading...'}</LoaderInline>
    </div>
  )

  const renderList = () => (
    <Suspense fallback={renderLoader()}>
      <ChangesListInner
        buildTypeId={buildTypeId}
        changes={changes.slice(0, visibleCount)}
        expandedChange={expandedChange}
        toggleExpandedChange={toggleExpandedChange}
      />
    </Suspense>
  )

  const renderNoChanges = () => (
    <div className={changesLogStyles.empty}>{'There are no changes found'}</div>
  )

  return (
    <div className={classes}>
      <div className={changesLogStyles.changesWrapper}>
        <div className={changesLogStyles.changes}>
          {changes.length > 0 ? renderList() : null}
          {changes.length === 0 && changesLoading ? renderLoader() : null}
          {isNoChanges ? renderNoChanges() : null}
        </div>
      </div>
      {expandedChange != null && (
        <ChangeDetailsPopup
          buildTypeId={buildTypeId}
          targetChangeId={expandedChange}
          show={withExpandableLines !== true}
          cancelDialog={toggleExpandedChange}
        />
      )}
      {withPager && (
        <Pager isLoading={changesLoading || changesReloading} group={PagerGroup.CHANGE} />
      )}
    </div>
  )
}

export default memo(ChangesList)
